import { useEffect, useRef, useState } from 'react';
import { D, formatMoney, PAYMENT_METHODS, type PaymentMethod } from '@makila/fiscal';
import { cartTotals, emitCreditNote, type IssuedDocument, type LicenceStatus, type Signer, type TerminalIdentity } from '@makila/pos-core';
import { Search, X } from 'lucide-react';
import { type DeviceState, lookupDocument } from '../lib/device';
import type { IdbTerminalStore } from '../lib/idb-store';
import type { Operator } from '../screens/Lock';
import { ManagerApproval } from './ManagerApproval';

const REFUND: PaymentMethod[] = ['NU', 'MP', 'EM', 'MK', 'CC', 'TB'];

/**
 * Devolução = nota de crédito (a única forma de corrigir um documento
 * emitido). Passos: autorização de um gerente → documento de origem → o que
 * volta → motivo e reembolso. Tudo o que o pos-core recusa (quantidade a
 * mais, sem motivo…) aparece aqui em português.
 */
export function ReturnDialog(p: {
  store: IdbTerminalStore; device: DeviceState; who: TerminalIdentity; signer: Signer; operator: Operator; cashSessionId: string; licence: LicenceStatus;
  /** exchange: troca — o valor fica para a compra seguinte, em vez de ser devolvido. */
  onDone: (nc: IssuedDocument, exchange: boolean) => void; onClose: () => void;
}) {
  const dlg = useRef<HTMLDialogElement>(null);
  const [approver, setApprover] = useState<Operator | null>(p.operator.role === 'manager' ? p.operator : null);
  const [number, setNumber] = useState('');
  const [recent, setRecent] = useState<IssuedDocument[]>([]);
  const [origin, setOrigin] = useState<{ doc: IssuedDocument; credited: Record<number, string> } | null>(null);
  const [qty, setQty] = useState<Record<number, string>>({});
  const [reason, setReason] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('NU');
  const [exchange, setExchange] = useState(false);
  const [nuit, setNuit] = useState('');
  const [ref, setRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    dlg.current?.showModal();
    void p.store.recentDocuments(30).then((d) => setRecent(d.filter((x) => x.docType === 'VD' || x.docType === 'FR')));
  }, [p.store]);

  const find = async (n: string) => {
    setError(null);
    setBusy(true);
    try {
      const local = await p.store.findDocument(n);
      if (local) { setOrigin({ doc: local, credited: {} }); return; }
      // Não é desta caixa: pergunta à nuvem (precisa de rede).
      const r = await lookupDocument(p.device, n);
      setOrigin({ doc: r.document, credited: r.alreadyCredited });
    } catch (e) {
      setError((e as { code?: string }).code === 'offline' ? 'Este documento não é desta caixa e não há internet para o procurar.' : (e as Error).message.includes('não encontrado') ? `Documento ${n} não encontrado.` : (e as Error).message);
    } finally { setBusy(false); }
  };

  const lines = origin?.doc.lines.map((l, i) => ({ l, i, q: new D(/^\d+(\.\d+)?$/.test(qty[i] ?? '') ? qty[i]! : 0) })).filter((x) => x.q.greaterThan(0)) ?? [];
  // Total exacto da NC: o mesmo cálculo da emissão (IVA incluído/excluído, desconto da linha de origem).
  let total = '0.00';
  try { total = cartTotals(lines.map((x) => { const { netAmount: _a, taxAmount: _b, totalAmount: _c, ...b } = x.l; return { ...b, quantity: x.q.toString() }; })).grossTotal; } catch { /* sem linhas */ }

  const onAccount = origin?.doc.docType === 'FT';
  const submit = async () => {
    if (!origin || !approver) return;
    setBusy(true); setError(null);
    try {
      const nc = await emitCreditNote(p.store, p.who, p.signer, {
        origin: origin.doc, alreadyCredited: origin.credited, lines: lines.map((x) => ({ originLine: x.i, quantity: x.q.toString() })),
        // Troca: o reembolso fica na gaveta, em numerário, e entra já como pagamento da compra nova.
        customerNuit: nuit || null,
        // FT a crédito: a NC abate a conta corrente do cliente (sem dinheiro).
        reason, refunds: onAccount ? [] : [{ method: exchange ? 'NU' : method, amount: total, reference: exchange ? 'Troca' : ref || null }], operatorId: p.operator.id, approvedBy: approver.id, cashSessionId: p.cashSessionId, licence: p.licence,
      });
      p.onDone(nc, exchange && !onAccount);
    } catch (e) { setError((e as Error).message); setBusy(false); }
  };

  return (
    <dialog ref={dlg} onCancel={(e) => { e.preventDefault(); if (!busy) p.onClose(); }} className="m-auto w-[min(720px,96vw)] rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]">
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="text-xl font-bold">Devolução (nota de crédito)</h2>
        <button aria-label="Fechar" onClick={p.onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="max-h-[75vh] overflow-y-auto p-5">
        {!approver && <ManagerApproval store={p.store} operator={p.operator} why="Uma devolução precisa da autorização de um gerente." onApproved={setApprover} />}

        {approver && !origin && (
          <div>
            <p className="text-sm text-muted">Autorizado por {approver.name}. Escreva o número do talão (ex.: VD L1C1 2026/15) ou escolha uma venda recente.</p>
            <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (number.trim()) void find(number); }}>
              <input autoFocus aria-label="Número do documento" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="N.º do documento" className="h-12 flex-1 rounded-xl border border-base bg-surface-2 px-4 font-mono" />
              <button disabled={busy} className="flex h-12 items-center gap-2 rounded-xl bg-charcoal px-4 font-semibold text-white"><Search size={18} /> Procurar</button>
            </form>
            <ul className="mt-3 divide-y divide-[var(--border)]">
              {recent.map((d) => (
                <li key={d.fullNumber}>
                  <button onClick={() => setOrigin({ doc: d, credited: {} })} className="flex h-12 w-full items-center justify-between px-2 text-left hover:bg-surface-2">
                    <span className="font-mono text-sm">{d.fullNumber}</span><span className="text-sm text-muted">{d.systemEntryDate.slice(11, 16)}</span><span className="num font-semibold">{formatMoney(d.grossTotal)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {approver && origin && (
          <div>
            <div className="font-semibold">{origin.doc.fullNumber} · {origin.doc.customer?.name ?? 'Consumidor Final'}</div>
            <ul className="mt-3 space-y-2">
              {origin.doc.lines.map((l, i) => {
                const left = new D(l.quantity).minus(origin.credited[i] ?? 0);
                return (
                  <li key={i} className="flex items-center gap-3 rounded-lg bg-surface-2 px-3 py-2">
                    <span className="flex-1">{l.description}<span className="block text-xs text-muted">vendido {String(l.quantity).replace('.', ',')} · pode devolver {left.toString().replace('.', ',')}</span></span>
                    <input inputMode="decimal" aria-label={`Devolver ${l.description}`} placeholder="0" value={qty[i] ?? ''} onChange={(e) => { setQty({ ...qty, [i]: e.target.value.replace(',', '.') }); setError(null); }}
                      className="num h-12 w-24 rounded-lg border border-base bg-[var(--surface)] px-2 text-right text-lg" />
                  </li>
                );
              })}
            </ul>
            {origin.doc.customer && !origin.doc.customer.nuit && (
              <input aria-label="NUIT do cliente" inputMode="numeric" placeholder={`NUIT de ${origin.doc.customer.name} (obrigatório na nota de crédito)`} value={nuit} onChange={(e) => setNuit(e.target.value)}
                className="mt-3 h-12 w-full rounded-xl border border-base bg-surface-2 px-4 font-mono" />
            )}
            <input aria-label="Motivo da devolução" placeholder="Motivo (obrigatório)" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-3 h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />
            {onAccount ? (
              <p role="status" className="mt-3 rounded-xl bg-sky-50 p-3 text-sm text-sky-900 dark:bg-sky-950 dark:text-sky-100">Factura a crédito: a devolução abate o que {origin.doc.customer?.name} deve. Não sai dinheiro da caixa.</p>
            ) : <>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => setExchange(true)} aria-pressed={exchange} className={`h-12 rounded-xl border px-3 text-sm font-medium ${exchange ? 'border-brand-700 bg-brand-700 text-white' : 'border-base'}`}>Troca (fica para outra compra)</button>
              {REFUND.map((m) => <button key={m} onClick={() => { setMethod(m); setExchange(false); }} className={`h-12 rounded-xl border px-3 text-sm font-medium ${!exchange && method === m ? 'border-brand-700 bg-brand-700 text-white' : 'border-base'}`}>{PAYMENT_METHODS[m]}</button>)}
            </div>
            {!exchange && method !== 'NU' && <input aria-label="Referência do reembolso" placeholder="Referência do reembolso" value={ref} onChange={(e) => setRef(e.target.value)} className="mt-2 h-12 w-full rounded-xl border border-base bg-surface-2 px-4 font-mono" />}
            </>}
            <div className="num mt-4 flex items-baseline justify-between text-2xl font-bold"><span>{onAccount ? 'A abater na conta' : exchange ? 'Crédito para a troca' : 'A devolver'}</span><span>{formatMoney(total)} MT</span></div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => { setOrigin(null); setQty({}); }} className="h-14 rounded-xl border border-base font-medium">Outro documento</button>
              <button disabled={busy || !lines.length} onClick={() => void submit()} className="h-14 rounded-xl bg-brand-700 font-bold text-white disabled:opacity-40">Emitir nota de crédito</button>
            </div>
          </div>
        )}
        {error && <p role="alert" className="mt-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
      </div>
    </dialog>
  );
}
